import { GearType } from './types'

export type GearSubtypeId = string

export type GearSubtype = {
  id: GearSubtypeId
  gearType: GearType
  label: string
  color: string
  /** Mechanical efficiency factor (0–1). Only meaningful for pulleys/descenders. */
  efficiency: number | null
  /** Icon key in GEAR_TO_ICON / ICON_MAP. Uses the parent gear icon. */
  iconKey: string
}

// ── Anchor subtypes ─────────────────────────────────────────────────────────
export const ANCHOR_SUBTYPES: GearSubtype[] = [
  { id: 'anchor-fixed',      gearType: 'anchor', label: 'Fixed',       color: '#2563eb', efficiency: null, iconKey: 'anchor' },
  { id: 'anchor-y-hang',     gearType: 'anchor', label: 'Y-Hang',      color: '#7c3aed', efficiency: null, iconKey: 'anchor' },
  { id: 'anchor-deviation',  gearType: 'anchor', label: 'Deviation',   color: '#0891b2', efficiency: null, iconKey: 'deviation' },
  { id: 'anchor-temporary',  gearType: 'anchor', label: 'Temporary',   color: '#ea580c', efficiency: null, iconKey: 'anchor' },
]

// ── Pulley subtypes ─────────────────────────────────────────────────────────
export const PULLEY_SUBTYPES: GearSubtype[] = [
  { id: 'pulley-high-eff',  gearType: 'pulley', label: 'High Efficiency', color: '#16a34a', efficiency: 0.95, iconKey: 'pulley' },
  { id: 'pulley-standard',  gearType: 'pulley', label: 'Standard',        color: '#475569', efficiency: 0.85, iconKey: 'pulley' },
  { id: 'pulley-low-eff',   gearType: 'pulley', label: 'Low Efficiency',  color: '#dc2626', efficiency: 0.70, iconKey: 'pulley' },
  { id: 'pulley-prusik',    gearType: 'pulley', label: 'Prusik Minding',  color: '#9333ea', efficiency: 0.90, iconKey: 'pulley' },
]

// ── Descender (ID) subtypes ─────────────────────────────────────────────────
export const DESCENDER_SUBTYPES: GearSubtype[] = [
  { id: 'descender-id',       gearType: 'descender', label: 'ID',         color: '#475569', efficiency: null, iconKey: 'descender-custom' },
  { id: 'descender-rig',      gearType: 'descender', label: 'Rig',        color: '#2563eb', efficiency: null, iconKey: 'descender-custom' },
  { id: 'descender-stop',     gearType: 'descender', label: 'Stop',       color: '#ea580c', efficiency: null, iconKey: 'descender-custom' },
  { id: 'descender-figure-8', gearType: 'descender', label: 'Figure 8',   color: '#64748b', efficiency: null, iconKey: 'descender-custom' },
]

/** All gear types that have sub-type catalogs. */
export const GEAR_TYPES_WITH_SUBTYPES: ReadonlySet<GearType> = new Set([
  'anchor', 'pulley', 'descender',
])

/** Ordered map: gearType → subtype list. */
export const GEAR_SUBTYPE_CATALOG: Partial<Record<GearType, GearSubtype[]>> = {
  anchor: ANCHOR_SUBTYPES,
  pulley: PULLEY_SUBTYPES,
  descender: DESCENDER_SUBTYPES,
}

const SUBTYPE_BY_ID: Map<GearSubtypeId, GearSubtype> = new Map()
for (const list of Object.values(GEAR_SUBTYPE_CATALOG)) {
  if (!list) continue
  for (const st of list) {
    SUBTYPE_BY_ID.set(st.id, st)
  }
}

export function getGearSubtype(subtypeId: GearSubtypeId | undefined): GearSubtype | null {
  if (!subtypeId) return null
  return SUBTYPE_BY_ID.get(subtypeId) ?? null
}
